/**
 * Canonical `GraphView` data shape for the Investigate workspace.
 *
 * A `GraphView` represents the case's graph state at a moment in time: nodes
 * (with full property snapshots), edges, and the user's view state (positions,
 * hides, notes). One shape for every producer (chip pattern queries, the
 * assistant `run_cypher` tool, future saved-view rehydration) and every
 * consumer (the five Investigate views).
 *
 * PARTNER FILE — `src/libs/services/spectre/services/graph_view.py`. The
 * Pydantic definitions there must stay structurally identical to these types.
 * Any change here MUST land in the same review as the matching change there;
 * otherwise producer drift breaks consumers at runtime.
 */

import type { SourceSnippet } from './source-snippet'

export type GraphViewSource = 'chips' | 'assistant' | 'saved-view'

export type LayoutHint = 'fcose' | 'breadthfirst' | 'cola' | 'concentric' | 'grid' | 'custom'

export interface Position {
  x: number
  y: number
}

export interface GraphViewMeta {
  source: GraphViewSource
  /** ISO-8601 UTC */
  createdAt: string
  caseId: string
  label?: string
  boundQueryId?: string
}

export interface GraphViewNode {
  id: string
  labels: string[]
  typeLabel?: string
  name?: string
  /** Full property snapshot at view-assembly time. */
  properties: Record<string, unknown>
}

export interface GraphViewEdge {
  id: string
  type: string
  source: string
  target: string
  properties: Record<string, unknown>
}

export interface GraphViewNote {
  id: string
  position: Position
  text: string
  authorId?: string
  /** ISO-8601 UTC */
  createdAt: string
}

export interface GraphViewState {
  /** Face evidence explicitly opened from an identity gallery. */
  expandedFaceIds?: string[]
  hiddenIds: string[]
  hiddenTypes: string[]
  hiddenRelationshipTypes: string[]
  positions: Record<string, Position>
  layoutHint?: LayoutHint
  notes: GraphViewNote[]
}

export interface GraphView {
  meta: GraphViewMeta
  nodes: GraphViewNode[]
  edges: GraphViewEdge[]
  /** Source snapshots are object-set members, separate from Neo4j node IDs. */
  snippets?: SourceSnippet[]
  view: GraphViewState
}

export function emptyViewState(): GraphViewState {
  return {
    hiddenIds: [],
    hiddenTypes: [],
    hiddenRelationshipTypes: [],
    positions: {},
    notes: [],
  }
}
